/* eslint-disable max-lines -- single inline timeline renderer; switch is intentionally flat so each entry kind is one obvious local edit */

/**
 * Renders the Projects sub-agent's Evidence timeline inline inside a
 * Projects message.
 *
 * Background
 * ----------
 * The Projects parent agent calls ``answer_with_evidence`` /
 * ``lookup_evidence`` which both dispatch to the Evidence chat loop
 * server-side. Each SSE chunk that loop yields is folded into a
 * canonical ``SessionConversation`` by the same
 * ``processChatChunk`` reducer the standalone Evidence chat uses
 * (see ``src/features/projects/state/evidence-sessions.ts``). What
 * this component renders is that ``SessionConversation.timeline`` —
 * so the in-Projects experience is byte-for-byte identical to what a
 * user sees in the Evidence chat surface.
 *
 * Why we don't mount ``ChatTimeline`` directly
 * --------------------------------------------
 * ``ChatTimeline`` is the full Evidence chat orchestrator: it pulls
 * in ``presentationAtom``, ``artefactPreviewAtom``, the slide-builder
 * follow-up controls, scroll tracking, and dozens of callback props.
 * None of that belongs in a Projects sub-agent inline view, and a lot
 * of it would fail to mount cleanly outside the Evidence chat tree.
 *
 * This component is the smaller cousin: a flat switch over
 * ``TimelineEntry.type`` that hands each entry to the matching leaf
 * block with the bare-minimum props it needs. The text body is
 * rendered via the canonical Evidence ``TextResponse`` component so
 * Streamdown / ``evidenceMdComponents`` / inline ``<cite-badge>``s /
 * the References Section / KeyPoints / markdown tables all match the
 * standalone Evidence chat (see the design-doc visual contract).
 * Earlier we used vanilla ``react-markdown`` + ``prose-sm`` which
 * dropped citation badges, rendered GFM tables as raw pipes, and lost
 * the References Section — fixed by EVI-projects-citations.
 *
 * Web-search blocks use the canonical V2 search-group rendering —
 * ``InlineSearchBlock`` per query, stacked inside a single bordered
 * container, wrapped in ``SearchGroupTransition`` which animates the
 * group from the streaming "searching…" rows into the collapsed
 * "N sources found" rollup via ``CollapsedSourcesSummary`` once all
 * searches complete. This mirrors ``ChatTimeline.renderSearchGroup``
 * (the V2 block-based path; see ``renderV2Timeline``). Earlier we
 * wrapped them in the V1 ``ActivityBox`` ("Thought for Ns" pill) +
 * ``SearchBlock`` (the older big-header rendering) which produced a
 * visibly different surface from the canonical Evidence chat. Library
 * search + patient summary entries render bare-stacked, exactly like
 * ``renderV2Timeline`` does — they are not part of the search-group
 * roll-up.
 *
 * The leaf blocks themselves (``InlineSearchBlock``,
 * ``LibrarySearchBlock``, ``CalculatorBlock``, etc.) are reused as-is
 * from the Evidence chat — the static-coupling test
 * (``__tests__/evidence_components_coupling.test.ts``) guards
 * against any future leaf component reaching back into an evidence-
 * chat-only context.
 *
 * Coverage policy
 * ---------------
 * Every entry type the Evidence loop emits today gets a dedicated
 * branch below. Anything unknown falls through to a tiny "step in
 * progress" pill so a new server-side chunk type cannot crash the
 * Projects inline render — it will look generic but still belong in
 * the timeline.
 */

import { AppointmentsBlock } from '@/components/chat/components/AppointmentsBlock';
import { ClinicalTaskResultBlock } from '@/components/chat/components/ClinicalTaskResultBlock';
import { CreateTemplateBlock } from '@/components/chat/components/CreateTemplateBlock';
import { EditTemplateSectionBlock } from '@/components/chat/components/EditTemplateSectionBlock';
import { PatientSearchBlock } from '@/components/chat/components/PatientSearchBlock';
import { SessionsByDateBlock } from '@/components/chat/components/SessionsByDateBlock';
import { TemplateSearchBlock } from '@/components/chat/components/TemplateSearchBlock';
import { TextResponse } from '@/components/chat/components/components';
import { useTextGrouping } from '@/components/chat/hooks/useTextGrouping';
import {
  ChatMode,
  type SessionConversation,
  type TextItemEntry,
  type TimelineEntry,
  type WebSearchItemEntry,
} from '@/components/chat/types/types';
import { IS_SLIDE_BUILDER_ENABLED } from '@/components/constants/flags';
import { SearchGroupTransition } from '@/components/ui/SearchGroupTransition';
import { EvidenceAnswerPortsHost } from '@/composition/hosts/evidence-answer-ports-host';
import { cn } from '@/lib/cn';
import {
  responseAnswerGapClass,
  useProseDensity,
  type ProseDensity,
} from '@heidi/answer-kit';
import { TypographyCaption } from '@heidi/design-system/typography';
import {
  ArtefactBlock,
  CalculatorBlock,
  CollapsedSourcesSummary,
  EmailPatientEvidenceBlock,
  FormulaCalculatorBlock,
  InlinePatientSummaryBlock,
  InlineSearchBlock,
  LibrarySearchBlock,
  PrintDocumentBlock,
} from '@heidi/evidence';
import type { ArtefactType } from '@heidi/evidence/contracts';
import { AlertTriangle, Loader2 } from 'lucide-react';

export interface EvidenceTimelineBodyProps {
  session: SessionConversation;
  /** Whether the sub-agent is still streaming (drives subtle pulse on
   *  the last entry). When ``false`` the timeline is terminal. */
  isStreaming?: boolean;
  /** Used by ``TextResponse`` for the Copy / Add-to-note action bar
   *  copy semantics and ``ChatMessageContext`` analytics. ``null`` is
   *  the honest default in Projects — there is no scribe session
   *  backing this evidence sub-agent call; passing the Projects chat
   *  id here would lie to ``useChatResponseActions`` about which
   *  scribe session the Add-to-note action should target. */
  sessionId?: string | null;
  /** Forwarded to ``TextResponse`` for the "New tab" document title
   *  when the user adds the evidence answer to a new chronicle. */
  chatTitle?: string;
  /** Project-chat opener for session rows. Standalone Evidence chat omits this
   *  and falls back to route navigation inside ``SessionsByDateBlock``. */
  onOpenSession?: (sessionId: string) => void;
  /** Project-chat opener for pptx export cards — opens the Slide Builder
   *  artifact panel. Surfaces without the artifact canvas (the overlay)
   *  omit this and the card opens the export URL instead. */
  onOpenPresentation?: (presentationId: string) => void;
  /** The step's Working row lists the searches and their sources, library
   *  documents included, so the card draws no search group or library block
   *  and holds only its answer. */
  searchesInWorkingBlock?: boolean;
  className?: string;
}

/** Group-position label for ``InlineSearchBlock`` — mirrors
 *  ``ChatTimeline.getGroupPosition`` so the bordered container's
 *  per-row affordances match the standalone surface exactly. */
const getGroupPosition = (
  idx: number,
  length: number
): 'first' | 'middle' | 'last' | 'only' => {
  if (length === 1) return 'only';
  if (idx === 0) return 'first';
  if (idx === length - 1) return 'last';
  return 'middle';
};

export const EvidenceTimelineBody = ({
  session,
  isStreaming = false,
  sessionId = null,
  chatTitle,
  onOpenSession,
  onOpenPresentation,
  searchesInWorkingBlock = false,
  className,
}: EvidenceTimelineBodyProps): JSX.Element | null => {
  const { timeline } = session;
  // This panel hosts Evidence prose inside a Work surface, so its column width
  // comes from the host, not from whether a scribe session backs the answer.
  const density = useProseDensity();
  // The Evidence backend emits each text delta as its own ``text_item``
  // (``_create_text_chunk`` in ``anthropic_evidence_tool.py`` bumps
  // ``chunk_index`` on every call), so the reducer leaves us with N
  // separate text entries tagged with the same ``groupId`` for one
  // synthesised response. Without merging, each entry renders as its
  // own block and the user sees the answer broken across newlines —
  // and worse, ``useSmoothStream`` inside ``TextResponse`` would
  // restart from the latest delta on every chunk, replacing prior
  // text instead of accumulating it. ``useTextGrouping`` is the same
  // hook ``ChatTimeline`` uses to fold the group back into one
  // ``TextResponse`` invocation.
  const { textGroups, getTextGroupInfo } = useTextGrouping(timeline);

  if (timeline.length === 0) {
    if (searchesInWorkingBlock) return null;
    return (
      <div className={cn('flex items-center gap-2 py-1', className)}>
        <Loader2 className="size-3.5 animate-spin text-text-tertiary" />
        <TypographyCaption className="text-text-tertiary">
          Starting Evidence search…
        </TypographyCaption>
      </div>
    );
  }

  // Build the rendered list with V2 search-group scan-ahead grouping:
  // when we hit a ``web_search_item`` we collect every contiguous
  // ``web_search_item`` that follows, render the whole group through
  // ``SearchGroupTransition``, and mark every collected position so
  // subsequent iterations skip them. Text groups render once at the
  // last in-group entry with the full chunk array so smooth-streaming
  // animates over the merged content. Mirrors
  // ``ChatTimeline.renderV2Timeline`` with the sub-agent's narrower
  // entry set (no user_message, no reasoning, no protocol tools).
  const rendered: JSX.Element[] = [];
  const renderedGroupIds = new Set<string>();
  const renderedWebSearchPositions = new Set<number>();

  timeline.forEach((entry, index) => {
    const isLast = index === timeline.length - 1;
    const isActive = isStreaming && isLast;

    // Suppress reasoning explicitly — the parent agent's surrounding
    // text already summarises the work; a dedicated reasoning pill
    // adds noise without driving any decision in Projects.
    if (entry.type === 'thinking_item' || entry.type === 'reasoning_item') {
      return;
    }

    if (entry.type === 'web_search_item') {
      if (renderedWebSearchPositions.has(entry.position)) return;

      // Scan forward to collect every contiguous web_search_item —
      // these all roll up into one ``SearchGroupTransition``.
      const searchGroup: WebSearchItemEntry[] = [entry];
      for (let i = index + 1; i < timeline.length; i++) {
        const next = timeline[i];
        if (next?.type === 'web_search_item') {
          searchGroup.push(next);
        } else {
          break;
        }
      }
      searchGroup.forEach((e) => renderedWebSearchPositions.add(e.position));
      if (searchesInWorkingBlock) return;

      // ``waitingForText`` keeps the per-row "searching…" animation up
      // until a text_item begins streaming after the group — without
      // it, completed searches sit idle while the synthesis spins,
      // making the surface look stuck. Mirrors the same gate in
      // ``ChatTimeline.renderV2Timeline``.
      const groupEndIdx = index + searchGroup.length - 1;
      const hasTextAfter = timeline
        .slice(groupEndIdx + 1)
        .some((e) => e.type === 'text_item');
      const waitingForText = isStreaming && !hasTextAfter;
      const allComplete =
        !waitingForText && searchGroup.every((s) => s.status === 'completed');
      const allSources = searchGroup.flatMap((s) => s.sources);

      rendered.push(
        <SearchGroupTransition
          key={`search-group-${entry.position}-${waitingForText}`}
          allComplete={allComplete}
          searchBlocks={
            <div className="flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border">
              {searchGroup.map((s, idx) => (
                <InlineSearchBlock
                  key={`search-${s.position}`}
                  query={s.query ?? ''}
                  sources={s.sources}
                  status={s.status}
                  isStreaming={isStreaming}
                  groupPosition={getGroupPosition(idx, searchGroup.length)}
                  sessionId={sessionId}
                />
              ))}
            </div>
          }
          summary={<CollapsedSourcesSummary sources={allSources} sessionId={sessionId} />}
        />
      );
      return;
    }

    // Library documents are listed on the step's Working row too.
    if (searchesInWorkingBlock && entry.type === 'library_search_item') return;

    const jsx = renderEntry({
      entry,
      session,
      isActive,
      sessionId,
      chatTitle,
      onOpenSession,
      onOpenPresentation,
      textGroups,
      getTextGroupInfo,
      renderedGroupIds,
      density,
    });
    if (jsx) rendered.push(jsx);
  });

  // Every entry here (search-group container, text body, library /
  // patient-summary / calculator / export blocks) is a facet of ONE answer, so
  // they take the within-answer rung. There is no question in this container —
  // it is an Evidence answer embedded in a Work turn, and the question sits a
  // level up in ``AgentTimeline`` — so it takes the rung alone rather than the
  // full turn class. This used to be a hand-picked ``gap-5`` chosen to
  // approximate what ``ChatTimeline`` did; all three surfaces now read the same
  // token, so they cannot drift apart again.
  return (
    <EvidenceAnswerPortsHost>
      <div
        className={cn(
          'flex w-full flex-col',
          responseAnswerGapClass(density),
          // Once the evidence has settled, freeze any in-progress loader still
          // animating inside it (a child entry whose status was cut mid-stream) so
          // nothing spins forever — generic across all entry types, no status mutation.
          !isStreaming &&
            '[&_.animate-pulse]:animate-none [&_.animate-spin]:animate-none',
          className
        )}
      >
        {rendered}
      </div>
    </EvidenceAnswerPortsHost>
  );
};

const entryKey = (entry: TimelineEntry, index: number): string => {
  if ('id' in entry && typeof entry.id === 'string') {
    return `${entry.type}:${entry.id}`;
  }
  return `${entry.type}:${index}`;
};

interface RenderEntryArgs {
  entry: TimelineEntry;
  session: SessionConversation;
  isActive: boolean;
  sessionId: string | null;
  chatTitle: string | undefined;
  onOpenSession: ((sessionId: string) => void) | undefined;
  onOpenPresentation: ((presentationId: string) => void) | undefined;
  textGroups: ReturnType<typeof useTextGrouping>['textGroups'];
  getTextGroupInfo: ReturnType<typeof useTextGrouping>['getTextGroupInfo'];
  renderedGroupIds: Set<string>;
  density: ProseDensity;
}

const renderEntry = (args: RenderEntryArgs): JSX.Element | null => {
  const {
    entry,
    session,
    isActive,
    sessionId,
    chatTitle,
    onOpenSession,
    onOpenPresentation,
    textGroups,
    getTextGroupInfo,
    renderedGroupIds,
    density,
  } = args;
  const key = entryKey(entry, entry.position);

  switch (entry.type) {
    case 'text_item': {
      // Render the entire group as one ``TextResponse`` at the last
      // in-group entry — passing every in-group entry as a chunk in
      // the ``chunks`` array. ``TextResponse``'s ``useSmoothStream``
      // animates over the concatenated chunk text, so prior chunks
      // stay visible while the latest delta streams in (the bug we
      // hit when we passed only ``entry.content``: each new chunk
      // restarted the smooth-stream from empty and the user saw
      // word-by-word replacement instead of accumulation). For
      // ungrouped text entries the chunk array is just ``[entry]``.
      // ``mode = ChatMode.EVIDENCE`` is what unlocks the cite-badge
      // injection + References Section path inside ``TextResponse``;
      // the reducer tags ``entry.mode`` with EVIDENCE already, but we
      // pass the resolved value explicitly so the inline render
      // doesn't depend on the reducer's tagging for a critical
      // visual feature.
      const groupEntries = resolveGroupEntries(
        entry,
        textGroups,
        getTextGroupInfo,
        renderedGroupIds
      );
      if (!groupEntries) return null;
      const lastEntry = groupEntries[groupEntries.length - 1] ?? entry;
      const merged = groupEntries.map((e) => e.content).join('');
      if (!merged) return null;
      return (
        <TextResponse
          key={key}
          sessionId={sessionId}
          density={density}
          chunks={groupEntries.map((e) => ({
            type: 'text' as const,
            text: e.content,
            index: e.backendIndex,
            annotations: e.annotations,
            citations: e.citations,
            groupId: e.groupId,
            id: e.id,
          }))}
          conversation={session}
          position={lastEntry.position}
          isStreaming={isActive}
          mode={lastEntry.mode ?? ChatMode.EVIDENCE}
          // ``isLastTextResponse`` controls the LEGACY-mode "Try with
          // Research" affordance — never relevant in the projects
          // inline view (always EVIDENCE here), so false is honest.
          isLastTextResponse={false}
          isLastInTextGroup={true}
          groupContent={merged}
          chatTitle={chatTitle}
        />
      );
    }

    // ``web_search_item`` is handled inline in the parent loop via
    // V2 scan-ahead grouping into ``SearchGroupTransition`` — the
    // entry never reaches this switch.

    case 'library_search_item': {
      return (
        <LibrarySearchBlock
          key={key}
          query={entry.query ?? ''}
          sources={entry.sources}
          status={entry.status}
        />
      );
    }

    case 'patient_summary_item': {
      return (
        <InlinePatientSummaryBlock key={key} status={entry.status} error={entry.error} />
      );
    }

    case 'calculator_item': {
      return (
        <CalculatorBlock
          key={key}
          calculator={entry.calculator}
          status={entry.status}
          result={entry.result}
          error={entry.error}
        />
      );
    }

    case 'clinical_task_result_item': {
      return (
        <ClinicalTaskResultBlock
          key={key}
          status={entry.status}
          tasks={entry.tasks}
          error={entry.error}
        />
      );
    }

    case 'email_patient_evidence_item': {
      return (
        <EmailPatientEvidenceBlock
          key={key}
          status={entry.status}
          patientEmail={entry.patientEmail ?? ''}
          error={entry.error}
          needsEmail={entry.needsEmail}
          needsPdf={entry.needsPdf}
        />
      );
    }

    case 'print_document_item': {
      return (
        <PrintDocumentBlock
          key={key}
          toolId={entry.toolId}
          status={entry.status}
          title={entry.title ?? 'Document'}
          url={entry.downloadUrl}
          filename={entry.filename}
          error={entry.error}
        />
      );
    }

    // Export blocks (PDF / Word / CSV / Excel / PPTX / patient PDF /
    // form-fill) — render the same rich ``ArtefactBlock`` the Evidence
    // chat uses (illustrated card + "Preview / Download" affordance)
    // so generated docs look identical in Projects AND the overlay.
    // Documents get no ``onPreview`` (the Evidence preview modal isn't
    // mounted in the Projects / overlay tree), so those cards open the
    // durable export URL in a new tab. The pptx card alone gets a
    // preview when the host passed ``onOpenPresentation`` (the project
    // chat, which mounts the Slide Builder artifact panel) and the
    // entry carries a ``presentationId``; the overlay omits the opener
    // and keeps the download fallback.
    case 'pdf_export_item':
    case 'patient_evidence_pdf_item':
    case 'word_export_item':
    case 'csv_export_item':
    case 'excel_export_item':
    case 'pptx_export_item':
    case 'form_fill_item': {
      const ex = entry as ExportEntryShape;
      const presentationId =
        entry.type === 'pptx_export_item' ? ex.presentationId : undefined;
      // Flag checked at CLICK time (not render time): PostHog flags load
      // asynchronously, so a render-time check on a freshly loaded page
      // would bake the card into download-only before flags resolve.
      const openPresentation =
        onOpenPresentation && presentationId
          ? () => {
              if (IS_SLIDE_BUILDER_ENABLED()) {
                onOpenPresentation(presentationId);
              } else if (ex.downloadUrl) {
                window.open(ex.downloadUrl, '_blank', 'noopener,noreferrer');
              }
            }
          : undefined;
      return (
        <ArtefactBlock
          key={key}
          artefactType={EXPORT_ARTEFACT_TYPE[entry.type] ?? 'pdf'}
          status={toArtefactStatus(ex.status)}
          title={ex.title || ex.filename || labelForExportType(entry.type)}
          url={ex.downloadUrl}
          error={ex.error}
          onPreview={openPresentation}
        />
      );
    }

    // Clinical data tools — reuse the same rich blocks the standalone
    // Evidence chat renders so "get appointments", "patients in a day",
    // patient-profile + template searches look identical in Projects
    // AND the overlay (both mount this body via
    // ``EvidenceSessionInlineBlock``). Each block is presentational —
    // it takes plain data props with no Evidence-chat context — so
    // it's safe outside the chat tree (guarded by the coupling test).
    case 'sessions_by_date_item': {
      return (
        <SessionsByDateBlock
          key={key}
          status={entry.status}
          date={entry.date}
          totalSessions={entry.totalSessions}
          hasMore={entry.hasMore}
          sessions={entry.sessions}
          error={entry.error}
          onOpenSession={onOpenSession}
        />
      );
    }

    case 'appointments_item': {
      return (
        <AppointmentsBlock
          key={key}
          status={entry.status}
          date={entry.date}
          totalAppointments={entry.totalAppointments}
          appointments={entry.appointments}
          error={entry.error}
        />
      );
    }

    case 'search_patient_profiles_item': {
      return (
        <PatientSearchBlock
          key={key}
          status={entry.status}
          query={entry.query}
          totalResults={entry.totalResults}
          error={entry.error}
        />
      );
    }

    case 'search_templates_item':
    case 'search_my_templates_item': {
      return (
        <TemplateSearchBlock
          key={key}
          variant="own"
          status={entry.status}
          query={entry.query}
          totalResults={entry.totalResults}
          error={entry.error}
        />
      );
    }

    case 'search_community_templates_item': {
      return (
        <TemplateSearchBlock
          key={key}
          variant="community"
          status={entry.status}
          query={entry.query}
          totalResults={entry.totalResults}
          error={entry.error}
        />
      );
    }

    case 'get_template_prefs_item': {
      return (
        <TemplateSearchBlock
          key={key}
          variant="own"
          purpose="fetch"
          status={entry.status === 'fetching' ? 'searching' : entry.status}
          templateAvailable={entry.templateAvailable}
          templateName={entry.templateName}
          error={entry.error}
        />
      );
    }

    case 'create_template_item': {
      return (
        <CreateTemplateBlock
          key={key}
          status={entry.status}
          templateId={entry.templateId}
          templateName={entry.templateName}
          error={entry.error}
        />
      );
    }

    case 'edit_template_section_item': {
      return (
        <EditTemplateSectionBlock
          key={key}
          status={entry.status}
          templateId={entry.templateId}
          templateName={entry.templateName}
          error={entry.error}
        />
      );
    }

    case 'formula_calculator_item': {
      return (
        <FormulaCalculatorBlock
          key={key}
          formulaName={entry.formulaName}
          formulaExpression={entry.formulaExpression}
          formulaDisplay={entry.formulaDisplay}
          category={entry.category}
          status={entry.status}
          variables={entry.variables}
          result={entry.result}
          sourceUrl={entry.sourceUrl}
          sourceName={entry.sourceName}
          validationReference={entry.validationReference}
          criteriaBreakdown={entry.criteriaBreakdown}
          calculationSteps={entry.calculationSteps}
          warnings={entry.warnings}
          error={entry.error}
        />
      );
    }

    // Remaining kinds (raw error, visualdx, interactive widgets,
    // protocol/capability tools, unsupported/unauthenticated tool
    // pills, quota) still fall through to a small one-line pill — they
    // either belong to a different feature surface (VisualDx) or aren't
    // exercised by the Projects / Evidence agent today.
    default:
      return <FallbackEntryPill key={key} entry={entry} isActive={isActive} />;
  }
};

/**
 * Resolve the in-group entries for a ``text_item`` and mark its
 * group as rendered so subsequent in-group entries no-op. Returns
 * ``null`` when the group has already been rendered (so this entry
 * should be skipped) or when there are no entries in the group.
 *
 * For ungrouped (legacy or single-chunk) text entries the array is
 * ``[entry]`` itself.
 */
const resolveGroupEntries = (
  entry: TextItemEntry,
  textGroups: ReturnType<typeof useTextGrouping>['textGroups'],
  getTextGroupInfo: ReturnType<typeof useTextGrouping>['getTextGroupInfo'],
  renderedGroupIds: Set<string>
): TextItemEntry[] | null => {
  if (!entry.groupId) return [entry];
  if (renderedGroupIds.has(entry.groupId)) return null;
  const groupInfo = getTextGroupInfo(entry);
  // Only render the group at its LAST in-group entry — rendering at
  // the first would attach the streaming pulse to a non-streaming
  // position; rendering at every in-group entry would duplicate the
  // merged content N times on screen.
  if (groupInfo && !groupInfo.isLastInGroup) return null;
  const group = textGroups.get(entry.groupId);
  if (!group || group.entries.length === 0) return null;
  renderedGroupIds.add(entry.groupId);
  return group.entries;
};

/** Structural view of the export timeline entries — they share these
 *  fields across chunk kinds (status literals vary; ``presentationId``
 *  is pptx-only). Localised cast keeps the ``ArtefactBlock`` mapping
 *  off the wide ``TimelineEntry`` union. */
interface ExportEntryShape {
  type: string;
  status: string;
  title?: string;
  filename?: string;
  downloadUrl?: string;
  error?: string;
  presentationId?: string;
}

const EXPORT_ARTEFACT_TYPE: Record<string, ArtefactType> = {
  pdf_export_item: 'pdf',
  patient_evidence_pdf_item: 'patient_handout',
  word_export_item: 'word',
  csv_export_item: 'csv',
  excel_export_item: 'excel',
  pptx_export_item: 'pptx',
  form_fill_item: 'form_fill',
};

/** Collapse the export chunks' wider status vocabulary
 *  ("filling" / "converting" / …) onto ``ArtefactBlock``'s tri-state. */
const toArtefactStatus = (status: string): 'generating' | 'completed' | 'error' => {
  if (status === 'completed') return 'completed';
  if (status === 'error' || status === 'failed') return 'error';
  return 'generating';
};

const labelForExportType = (type: string): string => {
  switch (type) {
    case 'pdf_export_item':
      return 'PDF export';
    case 'patient_evidence_pdf_item':
      return 'Patient evidence PDF';
    case 'word_export_item':
      return 'Word export';
    case 'csv_export_item':
      return 'CSV export';
    case 'excel_export_item':
      return 'Excel export';
    case 'pptx_export_item':
      return 'PowerPoint export';
    case 'form_fill_item':
      return 'Form fill';
    default:
      return 'Export';
  }
};

const FallbackEntryPill = ({
  entry,
  isActive,
}: {
  entry: TimelineEntry;
  isActive: boolean;
}): JSX.Element => {
  const status = 'status' in entry ? String(entry.status) : 'running';
  const isError = status === 'error' || status === 'failed';
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border bg-surface px-2.5 py-1.5 text-xs',
        isError
          ? 'border-validation-destructive-border text-validation-destructive'
          : 'border-border text-text-secondary'
      )}
    >
      {isError ? (
        <AlertTriangle className="size-3.5 shrink-0" />
      ) : (
        <Loader2
          className={cn(
            'size-3.5 shrink-0 text-text-tertiary',
            isActive && 'animate-spin'
          )}
        />
      )}
      <span className="truncate">{humaniseEntryType(entry.type)}</span>
    </div>
  );
};

const humaniseEntryType = (type: string): string =>
  type
    .replace(/_item$/, '')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
